/**
 * Resolve where to send a customer after they sign in.
 *
 * When a session expires mid-task, ProtectedRoute redirects to /login with the
 * intended location in `state.from` (and some flows pass an explicit
 * `state.returnTo`). To honour the original intent we restore the *full* path —
 * including query string and hash, which a deep link relies on — not just the
 * pathname. The candidate is validated as a same-origin relative path so a
 * crafted `returnTo` cannot be used as an open redirect.
 */

export interface ReturnToLocation {
  pathname?: string;
  search?: string;
  hash?: string;
}

export interface ReturnToState {
  returnTo?: string;
  from?: ReturnToLocation;
}

/** True when the string contains an ASCII control character (0x00-0x1F or 0x7F). */
function hasControlChars(value: string): boolean {
  for (let i = 0; i < value.length; i++) {
    const code = value.charCodeAt(i);
    if (code < 0x20 || code === 0x7f) return true;
  }
  return false;
}

/**
 * True when `path` is a safe same-origin relative path (e.g. "/orders/1?x=2").
 * Rejects absolute URLs, protocol-relative ("//host"), backslash tricks,
 * schemes ("javascript:") and control characters.
 */
export function isSafeRelativePath(
  path: string | null | undefined,
): path is string {
  if (!path) return false;
  if (!path.startsWith('/')) return false;
  if (path.startsWith('//')) return false;
  if (path.includes('\\')) return false;
  if (path.includes('://')) return false;
  if (hasControlChars(path)) return false;
  return true;
}

/** Build a full relative path from a location-like object, or undefined. */
function locationToPath(loc: ReturnToLocation | undefined): string | undefined {
  if (!loc?.pathname) return undefined;
  return `${loc.pathname}${loc.search ?? ''}${loc.hash ?? ''}`;
}

/**
 * Resolve the post-login destination. Prefers an explicit `returnTo`, falls back
 * to the captured `from` location, and finally to `fallback` ("/" by default)
 * when nothing safe is available.
 */
export function resolveReturnPath(
  state: ReturnToState | null | undefined,
  fallback = '/',
): string {
  const candidate = state?.returnTo ?? locationToPath(state?.from);
  return isSafeRelativePath(candidate) ? candidate : fallback;
}
